/**
 * CriterionCard — renders one of the three assessment criteria with
 * finding detail and collapsible source documents.
 *
 * Styled to match the summary-page group cards: subtle shadows,
 * hover elevation, status-aware backgrounds, smooth transitions.
 */

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Badge } from "@/ui/primitives/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { cn } from "@/utils/misc";
import {
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  FileText,
  Loader2,
  Shield,
  XCircle,
} from "lucide-react";
import type { CriterionConfig, CriterionDetail } from "./types";

interface SecuredInterestsFlag {
  flagged: boolean;
  detail: string;
}

interface CriterionCardProps {
  config: CriterionConfig;
  criterion?: CriterionDetail;
  sourceDocuments: Array<{ documentId: string; fileName: string }>;
  onViewDocument: (docId: string) => void;
  isRunning: boolean;
  securedInterestsFlag?: SecuredInterestsFlag;
}

export function CriterionCard({
  config,
  criterion,
  sourceDocuments,
  onViewDocument,
  isRunning,
  securedInterestsFlag,
}: CriterionCardProps) {
  const Icon = config.icon;

  // Pending state — no data yet
  if (!criterion) {
    return (
      <div
        className={cn(
          "overflow-hidden rounded-xl bg-background",
          "shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]",
          "hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]",
          "transition-all duration-200",
          isRunning &&
            "border border-blue-200 bg-blue-50/20 dark:border-blue-500/25 dark:bg-blue-500/10",
          !isRunning && "border border-border",
        )}
      >
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                isRunning ? "bg-blue-100 dark:bg-blue-500/15" : "bg-muted/50",
              )}
            >
              {isRunning ? (
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              ) : (
                <Icon className={cn("h-4 w-4", config.iconColor)} />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2.5">
                <span className="text-sm font-semibold text-foreground">
                  {config.label}
                </span>
                <Badge variant="secondary" size="sm">
                  Pending
                </Badge>
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                {config.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const met = criterion.met;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl bg-background",
        "shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]",
        "hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]",
        "transition-all duration-200",
        met
          ? "border border-red-200/60 dark:border-red-500/20"
          : "border border-emerald-200/60 dark:border-emerald-500/20",
      )}
    >
      {/* Header */}
      <div className="px-5 pb-4 pt-5">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
              met
                ? "bg-red-100 dark:bg-red-500/15"
                : "bg-emerald-100 dark:bg-emerald-500/15",
            )}
          >
            {met ? (
              <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-sm font-semibold text-foreground">
                {config.label}
              </span>
              <Badge color={met ? "red" : "green"} variant="soft" size="sm">
                {met ? "Met" : "Not Met"}
              </Badge>
              {criterion.confidence && (
                <Badge variant="outline" size="sm" className="capitalize">
                  {criterion.confidence}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Finding — formatted for readability */}
      <div className="px-5 pb-5">
        <div className="rounded-lg border border-border/50 bg-muted/30 p-3.5">
          <FormattedFinding text={criterion.detail} />
        </div>
      </div>

      {/* Secured interests flag — shown only on the Creditor Threshold card */}
      {securedInterestsFlag && (
        <div className="px-5 pb-4">
          <div
            className={cn(
              "flex items-start gap-3 rounded-lg border p-3.5",
              securedInterestsFlag.flagged
                ? "border-orange-200 bg-orange-50/50 dark:border-orange-500/20 dark:bg-orange-500/5"
                : "border-border/50 bg-muted/30",
            )}
          >
            <Shield
              className={cn(
                "mt-0.5 h-4 w-4 shrink-0",
                securedInterestsFlag.flagged
                  ? "text-orange-600 dark:text-orange-400"
                  : "text-muted-foreground/60",
              )}
            />
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-xs font-semibold text-foreground">
                  Secured Interests
                </span>
                <Badge
                  variant="soft"
                  size="sm"
                  color={securedInterestsFlag.flagged ? "orange" : "gray"}
                >
                  {securedInterestsFlag.flagged ? "Flagged" : "None Identified"}
                </Badge>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {securedInterestsFlag.detail}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Source documents — collapsible */}
      {sourceDocuments.length > 0 && (
        <div className="border-t border-border/60">
          <Collapsible>
            <CollapsibleTrigger className="group flex w-full items-center gap-2 px-5 py-3 text-left transition-colors duration-150 hover:bg-muted/30">
              <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 text-xs font-medium text-muted-foreground">
                {sourceDocuments.length} source document
                {sourceDocuments.length === 1 ? "" : "s"}
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/50 transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-1.5 px-5 pb-4">
                {sourceDocuments.map((doc) => (
                  <button
                    key={doc.documentId}
                    type="button"
                    onClick={() => onViewDocument(doc.documentId)}
                    className="group flex w-full items-center gap-2.5 rounded-lg border border-border/50 bg-background px-3 py-2.5 text-left transition-colors duration-150 hover:bg-muted/40"
                  >
                    <FileText className="h-3 w-3 shrink-0 text-muted-foreground/60" />
                    <span className="flex-1 truncate text-xs text-foreground/80">
                      {doc.fileName}
                    </span>
                    <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground/0 transition-colors duration-150 group-hover:text-muted-foreground" />
                  </button>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FormattedFinding — renders markdown finding text
// ---------------------------------------------------------------------------

function FormattedFinding({ text }: { text: string }) {
  return (
    <div className="prose-xs prose max-w-none text-xs leading-relaxed text-muted-foreground dark:prose-invert prose-headings:mb-1 prose-headings:mt-2 prose-headings:text-xs prose-headings:font-semibold prose-headings:text-foreground prose-p:my-1 prose-strong:text-foreground/90 prose-ol:my-1 prose-ul:my-1 prose-li:my-0.5 prose-hr:my-2">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}
